import { RestManager } from '@/newApi/services/rest/RestManager';
import type { ReviewEmploymentFormData, ReviewEmploymentChangeSummary, ReviewEmploymentUpdate, SyncData, ReviewEmploymentsOverviewTableData, ReviewEmploymentsResultsTableData, ReviewEmploymentAssesmentResult, ReviewEmploymentAttestations, ResultPerfomanceAssessmentKeyValues, ReviewEmploymentSalaryHistoryResult, ReviewEmploymentHourlySalaryKeyValues, ReviewEmploymentSalaryKeyValues } from '@sysarb/sysarb-types-v2/services/v1/pay-management';
import { getApiHost } from '@/appSettings';
import type { StorageFilterGroup } from '@sysarb/sysarb-types-v2/services/v1/filter-storage';
import type { Model, useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';

const payManagementService = new RestManager(`https://${getApiHost()}/pay-management-service`);

export function abort(): void {
    payManagementService.abort();
}

/**
 * Fetch employments connected to salary review
 * @param salaryReviewId Salary review id
 */
export async function getSalaryReviewEmploymentById(resgate: ReturnType<typeof useResgate>, salaryReviewEmploymentId: string): Promise<ReviewEmploymentFormData | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<ReviewEmploymentFormData>(`v1.pay-management.${instanceTopic}.review-employments.${salaryReviewEmploymentId}`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch employments connected to salary review
 * @param salaryReviewId Salary review id
 */
export async function getSalaryReviewEmployments(salaryReviewId: string, filter: StorageFilterGroup): Promise<ReviewEmploymentsOverviewTableData> {
    const { instance } = useLoginStore();
    return payManagementService.post<ReviewEmploymentsOverviewTableData>({ filter }, `v1/${instance}/pay-management/review-employments/${salaryReviewId}/overviewtabledata`);
}

/**
 * Update updateSalaryReviewEmployment.
 * @param payload employee data to update
 */
export function updateSalaryReviewEmployment(resgate: ReturnType<typeof useResgate>, payload: ReviewEmploymentUpdate): void {
    void resgate.call('v1.pay-management.review-employments', 'update', payload);
}

/**
 * Update updateSalaryReviewEmployment via a union role
 * @param payload employee data to update
 */
export async function updateSalaryReviewEmploymentViaUnion(resgate: ReturnType<typeof useResgate>, payload: ReviewEmploymentUpdate, currentOrganisationId: string): Promise<void | null> {
    const { instanceTopic } = useLoginStore();
    return resgate.call<void>(`v1.${instanceTopic}.pay-management.${currentOrganisationId}.review-employments`, 'updateviaunionrole', payload).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
}

/**
 * Fetch employments connected to salary review
 * @param salaryReviewId Salary review id
 */
export async function getResultTableData(salaryReviewId: string, filter: StorageFilterGroup): Promise<ReviewEmploymentsResultsTableData> {
    const { instance } = useLoginStore();
    return payManagementService.post<ReviewEmploymentsResultsTableData>({ filter }, `v1/${instance}/pay-management/review-employments/${salaryReviewId}/resultstabledata`);
}

/**
 * Fetch employments connected to salary review
 * @param salaryReviewId Salary review id
 */
export async function getAttestationTableData(salaryReviewEmploymentId: string): Promise<ReviewEmploymentAttestations[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<ReviewEmploymentAttestations[]>({}, `v1/${instance}/pay-management/review-employments/${salaryReviewEmploymentId}/attestationtabledata`);
}

/**
 * Fetch result performance chart data
 * @param salaryReviewEmploymentId Salary review employment id
 */
export async function getEmployeePerformanceChartData(salaryReviewEmploymentId: string): Promise<ReviewEmploymentAssesmentResult> {
    const { instance } = useLoginStore();
    return payManagementService.post<ReviewEmploymentAssesmentResult>({}, `v1/${instance}/pay-management/review-employments/${salaryReviewEmploymentId}/performanceassessmentchart`);
}

/**
 * Fetch result performance chart data
 * @param salaryReviewEmploymentId Salary review employment id
 */
export async function getEmployeeSalaryHistoryChartData(salaryReviewEmploymentId: string): Promise<ReviewEmploymentSalaryHistoryResult[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<ReviewEmploymentSalaryHistoryResult[]>({}, `v1/${instance}/pay-management/review-employments/${salaryReviewEmploymentId}/salaryhistorychart`);
}

/**
 * Fetch employment performance key values
 * @param salaryReviewId Salary review employment id
 */
export async function getEmploymentPerformanceKeyValues(resgate: ReturnType<typeof useResgate>, salaryReviewEmploymentId: string): Promise<ResultPerfomanceAssessmentKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<ResultPerfomanceAssessmentKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.review-employments.${salaryReviewEmploymentId}.performancekeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch employment performance key values
 * @param salaryReviewId Salary review employment id
 */
export async function getEmploymentHourlyKeyValues(resgate: ReturnType<typeof useResgate>, salaryReviewEmploymentId: string): Promise<ReviewEmploymentHourlySalaryKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<ReviewEmploymentHourlySalaryKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.review-employments.${salaryReviewEmploymentId}.hourlysalarykeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch employment performance key values
 * @param salaryReviewId Salary review employment id
 */
export async function getEmploymentMonthlyKeyValues(resgate: ReturnType<typeof useResgate>, salaryReviewEmploymentId: string): Promise<ReviewEmploymentSalaryKeyValues | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<ReviewEmploymentSalaryKeyValues>(`v1.pay-management.subdomain.${instanceTopic}.review-employments.${salaryReviewEmploymentId}.salarykeyvalues`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**********
* Sync
**********/

/**
 * Start syncing Salary review employment data.
 * @param reviewId Review id to sync data to
 */
export async function syncEmploymentData(resgate: ReturnType<typeof useResgate>, reviewId: string, salaryReviewEmploymentIdsToSync: string[]): Promise<SyncData | null> {
    const promise = resgate.call<SyncData>(`v1.pay-management.review-employments.${reviewId}`, 'sync', salaryReviewEmploymentIdsToSync).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Sync status summary of sync properties
 * @param reviewId Review id to sync data to
 */
export async function getSyncDataSummary(resgate: ReturnType<typeof useResgate>, salaryReviewEmploymentId: string): Promise<ReviewEmploymentChangeSummary | null> {
    const { instanceTopic } = useLoginStore();
    const promise = resgate.model<ReviewEmploymentChangeSummary>(`v1.pay-management.${instanceTopic}.review-employments.${salaryReviewEmploymentId}.changesummary`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch current syncdata
 * @param reviewId Review id to sync data to
 */
export async function getSyncData(resgate: ReturnType<typeof useResgate>, reviewId: string): Promise<Model<SyncData> | null> {
    const promise = resgate.model<SyncData>(`v1.pay-management.review-employments.${reviewId}.syncdata`).then(res => {
        if (res?.success === false) return null;
        return res.data;
    });
    return promise;
}

/**
 * Fetch employment ids that failed syncing
 * @param syncId SyncData id
 */
export async function getFailedSyncIds(syncId: string): Promise<string[]> {
    const { instance } = useLoginStore();
    return payManagementService.post<string[]>({}, `v1/${instance}/pay-management/review-employments/${syncId}/failedsync`);
}
